import { AsyncPipe, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  afterNextRender,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TuiDialogService, TuiLoader, TuiIcon } from '@taiga-ui/core';
import { Observable, filter } from 'rxjs';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AreasComponent } from '../areas/areas.component';
import { UserStore } from '../../../store/user.store';
import { IPersonData } from '../../interface/api/order-ttt.interface';
import { PhonePipe } from '../../helper/pipes/phone.pipe';
import { OidcLoginService } from '../../../core/services/oidc.login.service';
import { NavigationService } from './navigation.service';
import { CallComponent } from '../../../pages/main/call/call.component';
import { REFORCE_HOTLINE } from '../../helper/links';
import { MetrikaService, YM_GOALS } from '../../../core/services/metrika.service';

@Component({
  selector: 'app-header',
  imports: [
    AreasComponent,
    TuiIcon,
    TuiLoader,
    AsyncPipe,
    PhonePipe,
    CallComponent,
    RouterLink,
    RouterLinkActive,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected readonly hotline = REFORCE_HOTLINE;
  protected userInfo$!: Observable<IPersonData | null>;
  protected readonly isOpenPopupMenu = signal<boolean>(false);
  protected readonly isOpenProfilePopup = signal<boolean>(false);
  /** Десктопный бургер-дропдаун (Главная / Каталог) */
  protected readonly isOpenNavMenu = signal<boolean>(false);
  protected readonly isOpenAboutMenu = signal<boolean>(false);
  protected readonly loadingOIDC = signal<boolean>(false);
  private readonly userStore = inject(UserStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navService = inject(NavigationService);
  private readonly tuiDialogService = inject(TuiDialogService);
  private readonly oidcLoginService = inject(OidcLoginService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef);
  protected readonly metrika = inject(MetrikaService);
  protected readonly goals = YM_GOALS;
  protected readonly currentUrl = signal(this.router.url);
  protected readonly isScrolled = signal(false);
  protected readonly isWelcomeVisible = signal(false);
  private window?: Window;
  private welcomeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    afterNextRender(() => {
      this.window = this.document.defaultView ?? undefined;
      this.window?.addEventListener('scroll', this.onContentScroll, { passive: true });
      this.updateScrolledState();
    });
  }

  showDialogCall(content: TemplateRef<unknown>): void {
    this.metrika.reachGoal(this.goals.ctaCallback);
    this.tuiDialogService.open(content).subscribe();
  }

  public ngOnInit(): void {
    this.userInfo$ = this.userStore.user$;

    let hadUser = false;
    this.userInfo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userInfo) => {
      if (userInfo && !hadUser) {
        this.showWelcome();
      }
      hadUser = Boolean(userInfo);
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.closeBurgerMenu();
        this.isOpenNavMenu.set(false);
        this.isOpenAboutMenu.set(false);
      });
  }

  ngOnDestroy(): void {
    this.window?.removeEventListener('scroll', this.onContentScroll);
    if (this.welcomeTimer) {
      clearTimeout(this.welcomeTimer);
    }
    this.closeBurgerMenu();
  }

  protected closeWelcome(): void {
    this.isWelcomeVisible.set(false);
    if (this.welcomeTimer) {
      clearTimeout(this.welcomeTimer);
    }
  }

  private showWelcome(): void {
    this.isWelcomeVisible.set(true);
    if (this.welcomeTimer) {
      clearTimeout(this.welcomeTimer);
    }
    this.welcomeTimer = setTimeout(() => this.isWelcomeVisible.set(false), 3000);
  }

  private readonly onContentScroll = (): void => this.updateScrolledState();

  private updateScrolledState(): void {
    this.isScrolled.set((this.window?.scrollY ?? 0) > 12);
  }

  // Возврат «назад» с экрана OIDC восстанавливает страницу из bfcache с
  // зависшим лоадером у кнопок — сбрасываем его при показе страницы.
  @HostListener('window:pageshow')
  onPageShow(): void {
    this.loadingOIDC.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseBurger(): void {
    if (!this.isOpenPopupMenu() && !this.isOpenProfilePopup() && !this.isOpenAboutMenu()) {
      return;
    }
    this.closeBurgerMenu();
    this.closeProfilePopup();
    this.closeAboutMenu();
    this.isOpenAboutMenu.set(false);
  }

  // Бургер-попап — обычный якорный дропдаун (position: absolute от .hdr-right),
  // закрываем его по клику вне шапки, как и остальные попапы на странице.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpenPopupMenu() && !this.isOpenProfilePopup() && !this.isOpenAboutMenu()) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeBurgerMenu();
      this.closeProfilePopup();
      this.closeAboutMenu();
    }
  }

  /** redirectUrl — куда вернуться после OIDC; без него остаёмся на текущем роуте */
  public login(redirectUrl?: string): void {
    this.loadingOIDC.set(true);
    this.oidcLoginService.login(undefined, redirectUrl);
  }

  /** Вход из бургера — попап OIDC поверх страницы, бургер остаётся открытым */
  loginFromBurger(redirectUrl?: string): void {
    this.login(redirectUrl);
  }

  public logout(): void {
    this.loadingOIDC.set(true);
    this.closeBurgerMenu();
    this.closeProfilePopup();

    this.oidcLoginService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadingOIDC.set(false);
        this.router.navigateByUrl('/');
      });
  }

  protected getHeaderUserName(userInfo: IPersonData): string {
    const name = [userInfo.name, userInfo.second_name].filter(Boolean).join(' ');
    return name || [userInfo.surname, userInfo.name].filter(Boolean).join(' ') || 'Пользователь';
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  protected closeNavMenu(): void {
    this.isOpenNavMenu.set(false);
  }

  protected toggleAboutMenu(): void {
    this.isOpenAboutMenu.update((isOpen) => !isOpen);
  }

  protected closeAboutMenu(): void {
    this.isOpenAboutMenu.set(false);
  }

  protected isAboutRouteActive(): boolean {
    return this.currentUrl().startsWith('/contacts') || this.currentUrl().startsWith('/career');
  }

  onScrollClick(sectionId: string) {
    this.navService.navigateToHomeWithScroll(sectionId);
  }

  protected toggleBurgerMenu(): void {
    const isOpen = !this.isOpenPopupMenu();
    this.closeProfilePopup();
    this.isOpenPopupMenu.set(isOpen);
    this.document.body.classList.toggle('header-menu-open', isOpen);
  }

  protected toggleProfilePopup(): void {
    const isOpen = !this.isOpenProfilePopup();
    this.closeBurgerMenu();
    this.isOpenProfilePopup.set(isOpen);
  }

  protected closeProfilePopup(): void {
    this.isOpenProfilePopup.set(false);
  }

  protected closeBurgerMenu(): void {
    this.isOpenPopupMenu.set(false);
    this.document.body.classList.remove('header-menu-open');
  }

  navigateToAndCloseBurger(route: string): void {
    this.closeBurgerMenu();
    this.navigateTo(route);
  }

  /** Активен ли пункт меню для маршрута (аналог routerLinkActive для кнопок) */
  isActiveRoute(route: string): boolean {
    return this.currentUrl().startsWith(`/${route}`);
  }

  scrollSectionAndCloseBurger(sectionId: string): void {
    this.closeBurgerMenu();
    this.onScrollClick(sectionId);
  }
}
